import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Loader2, Star, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function KundaliMatchingPage({ user }) {
  const navigate = useNavigate();
  const [boy, setBoy] = useState({ name: '', dob: '', tob: '', pob: '' });
  const [girl, setGirl] = useState({ name: '', dob: '', tob: '', pob: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!boy.name || !boy.dob || !girl.name || !girl.dob) return;
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(`${API}/jyotish/matching`, { boy, girl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
    } catch { setResult({ error: true }); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-rose-50" data-testid="matching-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B2500] via-[#A0522D] to-[#8B4513] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/jyotish')} className="flex items-center gap-1 text-orange-200 text-sm mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Jyotish
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-7 h-7 text-pink-300" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Yatra One', serif" }}>Kundali Matching</h1>
          </div>
          <p className="text-orange-200 text-sm mt-1">Check Ashtakoot Guna Milan for marriage compatibility</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Boy Details */}
          <PersonForm label="Boy (Var)" labelHi="वर" data={boy} onChange={setBoy} color="blue" testPrefix="boy" />
          
          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-pink-200" />
            <Heart className="w-5 h-5 text-pink-400" />
            <div className="flex-1 h-px bg-pink-200" />
          </div>

          {/* Girl Details */}
          <PersonForm label="Girl (Vadhu)" labelHi="वधू" data={girl} onChange={setGirl} color="pink" testPrefix="girl" />

          <button
            type="submit"
            disabled={loading || !boy.name || !boy.dob || !girl.name || !girl.dob}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-[#8B2500] to-[#A0522D] text-white font-bold text-sm disabled:opacity-40 transition-all hover:shadow-lg active:scale-98"
            data-testid="match-submit-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Check Compatibility'}
          </button>
        </form>

        {/* Result */}
        {result && !result.error && !loading && (
          <div className="mt-6 bg-white rounded-2xl shadow-md border border-pink-100 overflow-hidden" data-testid="matching-result">
            <div className="px-5 py-4 bg-gradient-to-r from-pink-50 to-rose-50 border-b border-pink-100 text-center">
              <div className="text-4xl font-bold text-[#8B2500]">{result.total_points || 0}<span className="text-lg text-slate-400">/36</span></div>
              <p className="text-sm font-semibold mt-1" style={{ color: getMatchColor(result.total_points) }}>
                {getMatchLabel(result.total_points)}
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              {result.categories?.map((cat, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{cat.name}</p>
                    <p className="text-[11px] text-slate-400">{cat.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold text-[#8B2500]">{cat.obtained}</span>
                    <span className="text-xs text-slate-400">/{cat.max}</span>
                  </div>
                </div>
              ))}
              {result.recommendation && (
                <div className="pt-3 border-t border-orange-100">
                  <h4 className="text-xs font-bold text-[#8B2500] uppercase mb-1">Recommendation</h4>
                  <p className="text-sm text-slate-600">{result.recommendation}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {result?.error && !loading && (
          <div className="mt-6 text-center py-8"><p className="text-red-500">Failed to check compatibility. Please try again.</p></div>
        )}
      </div>
    </div>
  );
}

function PersonForm({ label, labelHi, data, onChange, color, testPrefix }) {
  const borderColor = color === 'blue' ? 'border-blue-200 focus:ring-blue-300' : 'border-pink-200 focus:ring-pink-300';
  const headerBg = color === 'blue' ? 'bg-blue-50' : 'bg-pink-50';
  const headerText = color === 'blue' ? 'text-blue-700' : 'text-pink-700';
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className={`px-4 py-2.5 ${headerBg} border-b border-slate-100`}>
        <span className={`text-sm font-bold ${headerText}`}>{label}</span>
        <span className="text-xs text-slate-400 ml-1">({labelHi})</span>
      </div>
      <div className="p-4 grid grid-cols-2 gap-3">
        <input type="text" placeholder="Full Name" value={data.name} onChange={e => onChange({...data, name: e.target.value})}
          className={`col-span-2 px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${borderColor}`} data-testid={`${testPrefix}-name`} />
        <input type="date" value={data.dob} onChange={e => onChange({...data, dob: e.target.value})}
          className={`px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${borderColor}`} data-testid={`${testPrefix}-dob`} />
        <input type="time" value={data.tob} onChange={e => onChange({...data, tob: e.target.value})} placeholder="Birth Time"
          className={`px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${borderColor}`} data-testid={`${testPrefix}-tob`} />
        <input type="text" placeholder="Place of Birth" value={data.pob} onChange={e => onChange({...data, pob: e.target.value})}
          className={`col-span-2 px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 ${borderColor}`} data-testid={`${testPrefix}-pob`} />
      </div>
    </div>
  );
}

function getMatchColor(points) {
  if (points >= 25) return '#16a34a';
  if (points >= 18) return '#ca8a04';
  return '#dc2626';
}
function getMatchLabel(points) {
  if (points >= 25) return 'Excellent Match! Highly Recommended';
  if (points >= 18) return 'Good Match. Recommended with minor remedies';
  if (points >= 10) return 'Average Match. Remedies advised';
  return 'Low Compatibility. Consult an astrologer';
}
